import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { useGetRolesQuery } from "@/services/rolesApi";
import {
  displayValue,
  formatDate
} from "@/lib/helper";
import TableFilters from "@/components/ui/table/data-table-filter";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { walletTransactionStatus } from "@/lib/constants/global";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { useGetRefundWalletRequestsQuery, useGetWalletRequestsQuery } from "@/services/walletApi";
import { RefundRequest, WalletRequest, WalletRequestStatus } from "@/types/wallet";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { Button } from "@/components/ui/button";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  role_id?: string[];
  sort_key: string;
  status?: boolean;
  search_key?: string;
  start_date?: string;
  end_date?: string;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "wallet_request",
};
export default function WalletRefundTableView() {
  const RefundReq = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).refund_req ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);

  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const {
    data: walletData,
    isLoading,
    refetch,
  } = useGetRefundWalletRequestsQuery(
    { org_id: orgId, query: query },
    {
      skip: query == "",
    }
  );

  const walletTableData = React.useMemo(() => {
    return Array.isArray(walletData?.data) ? walletData.data : [];
  }, [walletData]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const actionsColumn: ColumnDef<RefundRequest> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const status = row.original?.status;

      return status === WalletRequestStatus.PENDING ? (
        <DataTableRowActions
          access={RefundReq}
          data={row.original}
          refetch={refetch}
        />
      ) : null;
    },
  };
  const columns: ColumnDef<RefundRequest>[] = [
    {
      accessorFn: (row) => `${row.member_name}`,
      id: "full_name",
      meta: "member_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Name"
          sortKey="member_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 max-w-[150px]">
            <div className="w-full">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer truncate">
                      {displayValue(row.original.member_name ?? "")}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="capitalize">
                      {displayValue(row.original.member_name ?? "")}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.wallet_balance}`,
      id: "wallet_balance",
      meta: "wallet_balance",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Wallet Balance"
          sortKey="request_amount"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="pl-10">
              {displayValue(`${row.original.wallet_balance}`)}
            </div>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.amount}`,
      id: "amount",
      meta: "amount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Refund Amount"
          sortKey="amount"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <p className="capitalize cursor-pointer">
                {/* Display the truncated name */}
                {displayValue(`${row.original.amount}`, false)}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const value = row.original?.status.toLowerCase();
        const statusLabel = walletTransactionStatus.filter(
          (r) => r.value === value
        )[0];
        return (
          <span className="flex gap-2 items-center">
            <span
              className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
            ></span>
            <span>{statusLabel?.label}</span>
          </span>
        );
      },
    },
    {
      accessorKey: "submission_date",
      meta: "submission_date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Submission Date"
          sortKey="created_at"
          sortable={true}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <span className="flex gap-2 items-center">
            <span>{formatDate(row.original.created_at)}</span>
          </span>
        );
      },
    },
    ...(RefundReq !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: walletTableData as RefundRequest[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  function handleFilterChange(field: string, value: string | number) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { id: "Pending", name: "Pending" },
        { id: "Approved", name: "Approved" },
        { id: "Rejected", name: "Rejected" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
  ];

  const totalRecords = walletData?.filtered_counts ?? 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });
  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date
      ? formatDate(dates.start_date)
      : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    setFilterData((prev) => ({
      ...prev,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    }));

    setSearchCriteria((prev: any) => ({
      ...prev,
      start_date: formattedStartDate ?? undefined,
      // Only set end_date if start_date is present
      end_date:
        formattedStartDate && !formattedEndDate ? undefined : formattedEndDate,
      offset: 0,
      sort_key: "id",
      sort_order: "desc",
    }));
  };


  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 px-3">
        {/* Table Filter component */}
        <TableSearchFilter
          setSearchCriteria={setSearchCriteria}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />

        {/* Buttons Container */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-sm text-black flex items-center gap-1 lg:mb-0 h-8">
            <DatePickerWithRange
              name={"Date Range"}
              value={{
                start_date: filterData.start_date, // Access start_date directly
                end_date: filterData.end_date, // Access end_date directly
              }}
              onValueChange={(dates) => {
                handleDateRange(dates);
              }}
              label={"Select date range"}
              className="w-full" // Ensure full width
            />
          </div>
          <Button
            variant={"outline"}
            className="text-sm text-black flex items-center gap-1 lg:mb-0 h-8 px-2"
            onClick={() => {
              setFilterData({});
              setSearchCriteria(initialValue);
              setInputValue("");
            }}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="gap-2 flex justify-center items-center">
                    <i className="fa-solid fa-arrows-rotate"></i>
                    <span className="hidden sm:inline">Reset</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset Filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Button>
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center h-24">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : walletTableData.length > 0 ? (
          walletTableData.map((row: RefundRequest) => {
            const value = row.status.toLowerCase();
            const statusLabel = walletTransactionStatus.filter(
              (r) => r.value === value
            )[0];

            return (
              <div
                key={row.id}
                className="bg-white border rounded-lg p-4 shadow-sm space-y-2 cursor-pointer"
              >
                {/* Header row: member name + action dropdown if PENDING */}
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-base capitalize">{row.member_name || "—"}</h3>
                    <p className="text-xs text-gray-400">Wallet Bal: {row.wallet_balance ?? "—"}</p>
                  </div>
                  {row.status === WalletRequestStatus.PENDING && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <DataTableRowActions
                        access={RefundReq}
                        data={row}
                        refetch={refetch}
                      />
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex flex-col gap-1 pt-2 border-t text-sm text-gray-500">
                  <div className="flex justify-between items-center">
                    <span><strong>Refund Amount:</strong> {row.amount}</span>
                    <span className="flex gap-1.5 items-center">
                      <span className={`${statusLabel?.color} rounded-full w-2.5 h-2.5`}></span>
                      <span className="text-xs font-medium capitalize">{statusLabel?.label || row.status}</span>
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 pt-1">
                    <strong>Submission Date:</strong> {formatDate(row.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="h-24 text-center flex items-center justify-center border rounded-lg">
            No refund request found.
          </div>
        )}
      </div>

      {/* Desktop Table view */}
      <div className="hidden md:block rounded-none border border-border">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {/* <Table className="w-full overflow-x-scroll"> */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={
                      RefundReq !== "read"
                        ? "cursor-pointer hover:bg-secondary"
                        : ""
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : walletTableData.length > 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No wallet request found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No Request found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {walletTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
    </div>
  );
}